import { FormEvent, useCallback, useEffect, useRef, useState } from 'react';
import { fetchAccessLogs, fetchMyProfile, updateMyProfile, AccessLogItem } from '../../api/auth';
import { fetchBulletins } from '../../api/bulletin';
import {
  fetchLeaveRequests,
  fetchMyLeaveBalance,
} from '../../api/leaveRequest';
import {
  fetchMyEmployeeCard,
  fetchMyEmployeeHistory,
  fetchMyEmployeePhotoUrl,
} from '../../api/org';
import { fetchWorkReports } from '../../api/workReport';
import { companyRoleLabel } from '../../types/companyRole';
import { BulletinPost } from '../../types/bulletin';
import {
  LeaveBalance,
  LeaveRequest,
  formatLeaveDays,
  leaveStatusLabel,
  leaveTypeLabel,
} from '../../types/leaveRequest';
import { EmployeeCard, EmployeeHistoryItem } from '../../types/org';
import { WorkReport, workReportStatusLabel } from '../../types/workReport';
import '../org/OrgAdminPanel.css';
import '../company/CompanySettingsPanel.css';
import './MyProfilePanel.css';

import { reportError } from '../../utils/notify';
import { needsDisplayNameSetup } from '../../utils/displayName';
import ListPager from '../common/ListPager';
type DetailTab = 'info' | 'leave' | 'posts' | 'history';
type HistoryFilter = 'ALL' | 'DEPARTMENT' | 'ROLE' | 'JOIN' | 'LEAVE';

const HISTORY_FILTERS: { key: HistoryFilter; label: string }[] = [
  { key: 'ALL', label: '전체' },
  { key: 'DEPARTMENT', label: '인사배치' },
  { key: 'ROLE', label: '역할' },
  { key: 'JOIN', label: '입사' },
  { key: 'LEAVE', label: '연차' },
];

function errMessage(err: unknown, fallback: string) {
  return (
    (err as { response?: { data?: { message?: string } } })?.response?.data?.message ?? fallback
  );
}

function eventTypeLabel(type?: string | null) {
  switch (type) {
    case 'DEPARTMENT':
      return '인사배치';
    case 'ROLE':
      return '역할';
    case 'JOIN':
      return '입사';
    case 'DEACTIVATE':
      return '비활성화';
    case 'LEAVE':
      return '연차';
    default:
      return type || '이력';
  }
}

function formatDateTime(value?: string | null) {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString('ko-KR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatDate(value?: string | null) {
  if (!value) return '-';
  return value.slice(0, 10);
}

function deviceTypeLabel(value?: string | null) {
  switch (value) {
    case 'MOBILE':
      return '모바일';
    case 'TABLET':
      return '태블릿';
    case 'DESKTOP':
      return 'PC';
    case 'BOT':
      return '봇';
    default:
      return value || '-';
  }
}

function loginMethodLabel(value?: string | null) {
  switch (value) {
    case 'PASSWORD':
      return '아이디/비밀번호';
    case 'KAKAO':
      return '카카오';
    default:
      return value || '-';
  }
}

function MyProfilePanel() {
  const currentYear = new Date().getFullYear();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [tab, setTab] = useState<DetailTab>('info');

  const [card, setCard] = useState<EmployeeCard | null>(null);
  const [photoUrl, setPhotoUrl] = useState<string | null>(null);

  const [loginId, setLoginId] = useState('');
  const [nickname, setNickname] = useState('');
  const [profileImgUrl, setProfileImgUrl] = useState<string | null>(null);
  const [lastLoginAt, setLastLoginAt] = useState<string | null>(null);
  const [accessLogs, setAccessLogs] = useState<AccessLogItem[]>([]);
  const [accessPage, setAccessPage] = useState(0);
  const [accessTotalPages, setAccessTotalPages] = useState(0);
  const [accessTotalElements, setAccessTotalElements] = useState(0);
  const [accessLoading, setAccessLoading] = useState(false);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const previewUrlRef = useRef<string | null>(null);
  const photoObjectUrlRef = useRef<string | null>(null);

  const [leaveYear, setLeaveYear] = useState(currentYear);
  const [balance, setBalance] = useState<LeaveBalance | null>(null);
  const [leaves, setLeaves] = useState<LeaveRequest[]>([]);
  const [leaveLoading, setLeaveLoading] = useState(false);

  const [bulletins, setBulletins] = useState<BulletinPost[]>([]);
  const [workReports, setWorkReports] = useState<WorkReport[]>([]);
  const [postsLoading, setPostsLoading] = useState(false);

  const [historyFilter, setHistoryFilter] = useState<HistoryFilter>('ALL');
  const [histories, setHistories] = useState<EmployeeHistoryItem[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const revokePreview = useCallback(() => {
    if (previewUrlRef.current) {
      window.URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = null;
    }
    setPreviewUrl(null);
  }, []);

  const loadAccessLogs = useCallback(async (page = 0) => {
    try {
      setAccessLoading(true);
      const result = await fetchAccessLogs(page, 10);
      setAccessLogs(result.content || []);
      setAccessPage(result.number ?? page);
      setAccessTotalPages(result.totalPages ?? 0);
      setAccessTotalElements(result.totalElements ?? 0);
    } catch {
      reportError(setError, '접속 기록을 불러오지 못했습니다.');
    } finally {
      setAccessLoading(false);
    }
  }, []);

  const loadCore = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const [profile, employeeCard] = await Promise.all([
        fetchMyProfile(),
        fetchMyEmployeeCard(),
      ]);
      setLoginId((profile.loginId || '').trim());
      setNickname((profile.nickname || '').trim());
      setProfileImgUrl(profile.profileImg || null);
      setLastLoginAt(profile.lastLoginAt || null);
      setPendingFile(null);
      revokePreview();
      setCard(employeeCard);

      if (photoObjectUrlRef.current) {
        window.URL.revokeObjectURL(photoObjectUrlRef.current);
        photoObjectUrlRef.current = null;
      }
      if (employeeCard.hasPhoto) {
        try {
          const url = await fetchMyEmployeePhotoUrl();
          photoObjectUrlRef.current = url;
          setPhotoUrl(url);
        } catch {
          setPhotoUrl(employeeCard.profileImageUrl || profile.profileImg || null);
        }
      } else {
        setPhotoUrl(employeeCard.profileImageUrl || profile.profileImg || null);
      }

      await loadAccessLogs(0);
    } catch {
      reportError(setError, '내 정보를 불러오지 못했습니다.');
      setCard(null);
    } finally {
      setLoading(false);
    }
  }, [loadAccessLogs, revokePreview]);

  useEffect(() => {
    void loadCore();
    return () => {
      revokePreview();
      if (photoObjectUrlRef.current) {
        window.URL.revokeObjectURL(photoObjectUrlRef.current);
        photoObjectUrlRef.current = null;
      }
    };
  }, [loadCore, revokePreview]);

  useEffect(() => {
    if (tab !== 'leave' || !card) return;
    let cancelled = false;
    const load = async () => {
      try {
        setLeaveLoading(true);
        const [bal, leavePage] = await Promise.all([
          fetchMyLeaveBalance(leaveYear),
          fetchLeaveRequests({ page: 0, size: 50 }),
        ]);
        if (cancelled) return;
        setBalance(bal);
        setLeaves((leavePage.content || []).filter((row) => row.requesterUserId === card.userId));
        setError('');
      } catch {
        if (!cancelled) reportError(setError, '연차·휴가 정보를 불러오지 못했습니다.');
      } finally {
        if (!cancelled) setLeaveLoading(false);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [tab, leaveYear, card]);

  useEffect(() => {
    if (tab !== 'posts' || !card) return;
    let cancelled = false;
    const load = async () => {
      try {
        setPostsLoading(true);
        const [bulletinPage, reportPage] = await Promise.all([
          fetchBulletins({ mine: true, page: 0, size: 50 }),
          fetchWorkReports({ page: 0, size: 50 }),
        ]);
        if (cancelled) return;
        setBulletins(bulletinPage.content || []);
        setWorkReports((reportPage.content || []).filter((row) => row.authorUserId === card.userId));
        setError('');
      } catch {
        if (!cancelled) reportError(setError, '작성글을 불러오지 못했습니다.');
      } finally {
        if (!cancelled) setPostsLoading(false);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [tab, card]);

  useEffect(() => {
    if (tab !== 'history') return;
    let cancelled = false;
    const load = async () => {
      try {
        setHistoryLoading(true);
        const list = await fetchMyEmployeeHistory(
          historyFilter === 'ALL' ? undefined : historyFilter
        );
        if (cancelled) return;
        setHistories(list);
        setError('');
      } catch {
        if (!cancelled) reportError(setError, '이력을 불러오지 못했습니다.');
      } finally {
        if (!cancelled) setHistoryLoading(false);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [tab, historyFilter]);

  const handlePickFile = (file: File | null) => {
    revokePreview();
    setPendingFile(file);
    if (!file) return;
    const url = window.URL.createObjectURL(file);
    previewUrlRef.current = url;
    setPreviewUrl(url);
  };

  const handleSubmitAccount = async (event: FormEvent) => {
    event.preventDefault();
    const name = nickname.trim();
    if (needsDisplayNameSetup(name)) {
      reportError(setError, '이름은 2자 이상의 본인 실명으로 입력해 주세요. (`.` 등은 사용할 수 없습니다)');
      setSuccess('');
      return;
    }
    try {
      setSaving(true);
      await updateMyProfile({
        nickname: name,
        profileImg: pendingFile,
      });
      setSuccess('계정 정보가 저장되었습니다. 기본정보 이름도 함께 반영됩니다.');
      setError('');
      window.dispatchEvent(new CustomEvent('linelog:profile-name', { detail: name }));
      await loadCore();
    } catch (err) {
      setSuccess('');
      reportError(setError, errMessage(err, '저장에 실패했습니다.'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <p className="contract-panel__hint">불러오는 중...</p>;
  }

  if (!card) {
    return (
      <div className="org-card my-profile-card">
        <p className="contract-panel__error">{error || '내 정보를 찾을 수 없습니다.'}</p>
      </div>
    );
  }

  const displayName = nickname || card.displayName || card.nickname || `사용자 ${card.userId}`;
  const headerPhoto = photoUrl;
  const accountImg = previewUrl || profileImgUrl;

  return (
    <div className="org-card my-profile-card">
      <header className="org-card__header">
        <div className="org-card__identity">
          <div className="org-card__photo">
            {headerPhoto ? (
              <img src={headerPhoto} alt={displayName} />
            ) : (
              <span>{displayName.slice(0, 1)}</span>
            )}
          </div>
          <div>
            <p className="org-admin__eyebrow">내정보</p>
            <h2>{displayName}</h2>
            <p>
              {companyRoleLabel(card.role)}
              {card.jobTitle ? ` · ${card.jobTitle}` : ''}
              {card.departmentName ? ` · ${card.departmentName}` : ' · 미배치'}
            </p>
          </div>
        </div>
      </header>

      {error ? <p className="contract-panel__error">{error}</p> : null}
      {success ? <p className="company-settings__success">{success}</p> : null}

      <div className="org-card__tabs">
        <button
          type="button"
          className={tab === 'info' ? 'is-active' : ''}
          onClick={() => setTab('info')}
        >
          기본정보
        </button>
        <button
          type="button"
          className={tab === 'leave' ? 'is-active' : ''}
          onClick={() => setTab('leave')}
        >
          연차·휴가
        </button>
        <button
          type="button"
          className={tab === 'posts' ? 'is-active' : ''}
          onClick={() => setTab('posts')}
        >
          작성글
        </button>
        <button
          type="button"
          className={tab === 'history' ? 'is-active' : ''}
          onClick={() => setTab('history')}
        >
          이력
        </button>
      </div>

      {tab === 'info' ? (
        <div className="org-card__grid">
          <section>
            <h3>기본 정보</h3>
            <dl>
              <div>
                <dt>이름</dt>
                <dd>{nickname || card.displayName || card.nickname || '-'}</dd>
              </div>
              <div>
                <dt>사번</dt>
                <dd>{card.employeeNo || '-'}</dd>
              </div>
              <div>
                <dt>담당</dt>
                <dd>{card.jobTitle || '-'}</dd>
              </div>
              <div>
                <dt>역할</dt>
                <dd>{companyRoleLabel(card.role)}</dd>
              </div>
              <div>
                <dt>부서</dt>
                <dd>{card.departmentName || '미배치'}</dd>
              </div>
            </dl>
          </section>

          <section>
            <h3>연락 / 인사</h3>
            <dl>
              <div>
                <dt>이메일</dt>
                <dd>{card.email || '-'}</dd>
              </div>
              <div>
                <dt>연락처</dt>
                <dd>{card.phone || '-'}</dd>
              </div>
              <div>
                <dt>생년월일</dt>
                <dd>{card.birthDate || '-'}</dd>
              </div>
              <div>
                <dt>입사일</dt>
                <dd>{card.hireDate || '-'}</dd>
              </div>
              <div>
                <dt>주소</dt>
                <dd>{card.address || '-'}</dd>
              </div>
            </dl>
          </section>

          <section className="is-full my-profile-card__account">
            <h3>계정 설정</h3>
            <p className="org-card__memo my-profile-card__hint">
              여기서 바꾼 이름이 기본정보·헤더·조직 표시에 그대로 반영됩니다. 사번·입사일 등 인사
              정보는 관리자가 직원관리에서 등록합니다.
            </p>
            <form className="my-profile-card__account-form" onSubmit={(e) => void handleSubmitAccount(e)}>
              <div className="company-settings__grid">
                <label className="company-settings__field">
                  <span>로그인 ID</span>
                  <input type="text" value={loginId || '-'} disabled />
                  {loginId.startsWith('kakao_') ? (
                    <em className="company-settings__counter">
                      카카오 로그인 계정용 고유 ID입니다. 변경할 수 없습니다.
                    </em>
                  ) : null}
                </label>
                <label className="company-settings__field">
                  <span>이름 *</span>
                  <input
                    type="text"
                    value={nickname}
                    onChange={(e) => setNickname(e.target.value)}
                    maxLength={50}
                    placeholder="표시 이름"
                    required
                  />
                </label>
              </div>

              <div className="company-settings__logo-block my-profile__photo-block">
                <div className="my-profile__avatar my-profile__avatar--lg">
                  {accountImg ? <img src={accountImg} alt="프로필 미리보기" /> : '사진'}
                </div>
                <div className="company-settings__logo-actions">
                  <p className="company-settings__logo-meta">
                    {pendingFile ? pendingFile.name : '프로필 사진'}
                  </p>
                  <div className="company-settings__btn-row">
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      hidden
                      onChange={(e) => handlePickFile(e.target.files?.[0] ?? null)}
                    />
                    <button
                      type="button"
                      className="company-settings__btn company-settings__btn--primary"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      사진 선택
                    </button>
                    {pendingFile ? (
                      <button
                        type="button"
                        className="company-settings__btn company-settings__btn--ghost"
                        onClick={() => {
                          handlePickFile(null);
                          if (fileInputRef.current) fileInputRef.current.value = '';
                        }}
                      >
                        선택 취소
                      </button>
                    ) : null}
                  </div>
                </div>
              </div>

              <div className="my-profile-card__account-actions">
                <button
                  type="submit"
                  className="company-settings__btn company-settings__btn--primary"
                  disabled={saving}
                >
                  {saving ? '저장 중...' : '계정 정보 저장'}
                </button>
              </div>
            </form>
          </section>

          <section className="is-full my-profile-card__access">
            <h3>접속 기록</h3>
            <p className="org-card__memo my-profile-card__hint">
              마지막 접속: {formatDateTime(lastLoginAt)}
            </p>
            {accessLoading && accessLogs.length === 0 ? (
              <p className="contract-panel__hint">불러오는 중...</p>
            ) : accessLogs.length === 0 ? (
              <p className="contract-panel__hint">아직 기록된 접속이 없습니다.</p>
            ) : (
              <div className="paged-list paged-list--compact">
                <div className="paged-list__scroll">
                  <table className="my-profile-card__table">
                    <thead>
                      <tr>
                        <th>접속 시각</th>
                        <th>방식</th>
                        <th>기기</th>
                        <th>브라우저</th>
                        <th>OS</th>
                        <th>IP</th>
                      </tr>
                    </thead>
                    <tbody>
                      {accessLogs.map((log) => (
                        <tr key={log.accessLogId ?? `${log.accessedAt}-${log.ipAddress}`}>
                          <td>{formatDateTime(log.accessedAt)}</td>
                          <td>{loginMethodLabel(log.loginMethod)}</td>
                          <td>{deviceTypeLabel(log.deviceType)}</td>
                          <td>{log.browser || '-'}</td>
                          <td>{log.os || '-'}</td>
                          <td>{log.ipAddress || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <ListPager
                  page={accessPage}
                  totalPages={accessTotalPages}
                  totalElements={accessTotalElements}
                  loading={accessLoading}
                  onChange={(next) => void loadAccessLogs(next)}
                />
              </div>
            )}
          </section>
        </div>
      ) : null}

      {tab === 'leave' ? (
        <div className="org-card__grid">
          <section className="is-full">
            <div className="org-leave-head">
              <h3>연차 ({leaveYear})</h3>
              <select
                value={leaveYear}
                onChange={(e) => setLeaveYear(Number(e.target.value))}
              >
                {[currentYear - 1, currentYear, currentYear + 1].map((y) => (
                  <option key={y} value={y}>
                    {y}년
                  </option>
                ))}
              </select>
            </div>
            {leaveLoading ? (
              <p className="contract-panel__hint">불러오는 중...</p>
            ) : (
              <div className="org-leave-summary">
                <div>
                  <span>부여</span>
                  <strong>{formatLeaveDays(balance?.grantedDays)}일</strong>
                </div>
                <div>
                  <span>사용</span>
                  <strong>{formatLeaveDays(balance?.usedDays)}일</strong>
                </div>
                <div className="is-remain">
                  <span>잔여</span>
                  <strong>{formatLeaveDays(balance?.remainingDays)}일</strong>
                </div>
              </div>
            )}
            <p className="org-card__memo my-profile-card__hint">
              신청·수정은 마이페이지의 「휴가·출장」 메뉴에서 할 수 있습니다.
            </p>
          </section>

          <section className="is-full">
            <h3>휴가·출장 이력</h3>
            {leaveLoading ? (
              <p className="contract-panel__hint">불러오는 중...</p>
            ) : leaves.length === 0 ? (
              <div className="org-history__empty">
                <strong>이력이 없습니다</strong>
                <p>신청한 휴가·출장이 여기에 표시됩니다.</p>
              </div>
            ) : (
              <div className="my-profile-card__table-wrap">
                <table className="my-profile-card__table">
                  <thead>
                    <tr>
                      <th>유형</th>
                      <th>기간</th>
                      <th>일수</th>
                      <th>상태</th>
                      <th>사유</th>
                    </tr>
                  </thead>
                  <tbody>
                    {leaves.map((row) => (
                      <tr key={row.leaveRequestId}>
                        <td>{leaveTypeLabel(row.leaveType)}</td>
                        <td>
                          {formatDate(row.startDate)}
                          {row.startDate !== row.endDate ? ` ~ ${formatDate(row.endDate)}` : ''}
                        </td>
                        <td>{formatLeaveDays(row.days)}일</td>
                        <td>
                          <span className={`my-profile-card__badge status-${row.status}`}>
                            {leaveStatusLabel(row.status)}
                          </span>
                        </td>
                        <td>{row.reason || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </div>
      ) : null}

      {tab === 'posts' ? (
        <div className="org-card__grid">
          <section className="is-full">
            <h3>회사 게시판</h3>
            {postsLoading ? (
              <p className="contract-panel__hint">불러오는 중...</p>
            ) : bulletins.length === 0 ? (
              <div className="org-history__empty">
                <strong>작성한 게시글이 없습니다</strong>
                <p>게시판에 올린 글이 여기에 쌓입니다.</p>
              </div>
            ) : (
              <ul className="my-profile-card__list">
                {bulletins.map((post) => (
                  <li key={post.postId}>
                    <strong>{post.title}</strong>
                    <span>{formatDateTime(post.createdAt)}</span>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section className="is-full">
            <h3>업무보고서</h3>
            <p className="org-card__memo my-profile-card__hint">
              작성·결재는 「업무보고서」 메뉴에서 진행하세요.
            </p>
            {postsLoading ? (
              <p className="contract-panel__hint">불러오는 중...</p>
            ) : workReports.length === 0 ? (
              <div className="org-history__empty">
                <strong>작성한 업무보고서가 없습니다</strong>
                <p>제출한 업무보고서가 여기에 표시됩니다.</p>
              </div>
            ) : (
              <div className="my-profile-card__table-wrap">
                <table className="my-profile-card__table">
                  <thead>
                    <tr>
                      <th>제목</th>
                      <th>보고일</th>
                      <th>상태</th>
                      <th>작성일</th>
                    </tr>
                  </thead>
                  <tbody>
                    {workReports.map((row) => (
                      <tr key={row.workReportId}>
                        <td>{row.title}</td>
                        <td>{formatDate(row.reportDate)}</td>
                        <td>
                          <span className={`my-profile-card__badge status-${row.status}`}>
                            {workReportStatusLabel(row.status, row.approvalInProgress)}
                          </span>
                        </td>
                        <td>{formatDateTime(row.createdAt)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </div>
      ) : null}

      {tab === 'history' ? (
        <div className="org-history">
          <div className="org-history__filters">
            {HISTORY_FILTERS.map((item) => (
              <button
                key={item.key}
                type="button"
                className={historyFilter === item.key ? 'is-active' : ''}
                onClick={() => setHistoryFilter(item.key)}
              >
                {item.label}
              </button>
            ))}
          </div>

          {historyLoading ? (
            <p className="contract-panel__hint">이력을 불러오는 중...</p>
          ) : histories.length === 0 ? (
            <div className="org-history__empty">
              <strong>이력이 없습니다</strong>
              <p>인사배치·역할 변경·입사·연차 부여/사용 기록이 여기에 쌓입니다.</p>
            </div>
          ) : (
            <ul className="org-history__list">
              {histories.map((item) => (
                <li key={item.historyId}>
                  <div className="org-history__meta">
                    <span className="org-history__type">{eventTypeLabel(item.eventType)}</span>
                    <time>{formatDateTime(item.createdAt)}</time>
                  </div>
                  <strong>{item.title}</strong>
                  <p>{item.description || '-'}</p>
                  {(item.fromValue || item.toValue) && (
                    <em>
                      {item.fromValue || '-'} → {item.toValue || '-'}
                    </em>
                  )}
                  {item.createdByName && (
                    <span className="org-history__by">처리: {item.createdByName}</span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : null}
    </div>
  );
}

export default MyProfilePanel;
